import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import DataDisclosure from '../Inventory/DataDisclosure';
import { fmtMoney, fmtMoneyExact } from './format';
import { c, t, sp, r, card, chart, tbl } from '../../theme';

// ── Overtime against the annual budget ───────────────────────────────────────
//
// Deliberately its own panel rather than a budget line drawn on OvertimeTrend,
// and the reason is the filters. The trend narrows with the lens, the division
// and the labor category; the budget is a single figure per cost center per
// month and cannot narrow with any of them. A budget line on a filtered trend
// would sit above a shrunken actual and read as a saving that is really just a
// filter. This panel says what it covers and ignores the lens entirely.
//
// The month mismatch is the other thing on screen. A month of ACTUALS is the
// four or five weekly pay runs that ENDED in it -- the rule the whole route
// uses -- while a month of BUDGET is a calendar month. A five-run month runs
// about a quarter over for no reason anybody can act on, so the run count sits
// under every month and the year-to-date pair, which is immune, leads.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// How many weekly runs a month should hold, from the weekday payroll actually
// lands on. Used only to say whether the newest month is still filling up: a
// month showing 2 of 4 runs is not 50% under budget, it is half over.
function expectedRuns(lastPeriodEnd, month, year) {
  if (!lastPeriodEnd) return null;
  const weekday = new Date(`${String(lastPeriodEnd).slice(0, 10)}T00:00:00`).getDay();
  let count = 0;
  const d = new Date(year, month - 1, 1);
  while (d.getMonth() === month - 1) {
    if (d.getDay() === weekday) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// One chart definition for both scales. The company's twelve bars and a single
// cost center's twelve bars are the same statement about different rows, and a
// reader who learns to read one has learned to read the other -- which stops
// being true the moment they are two components maintained apart.
function MonthChart({ rows, year, height = 220 }) {
  return (
    <div style={s.chartWrap}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid vertical={false} stroke={chart.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }}
                 axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 10, fill: chart.axisText }}
                 axisLine={false} tickLine={false} width={64} />
          <Tooltip
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            formatter={(v, name) => [v === null ? '—' : fmtMoneyExact(v), name]}
            labelFormatter={(label, p) => {
              const row = p?.[0]?.payload;
              if (!row || !row.period_count) return `${label} ${year}`;
              return `${label} ${year} · ${row.period_count} pay run${row.period_count === 1 ? '' : 's'}`;
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* Budget as the line and actual as the bar, not the other way
              round: the budget is the level being tested and the spend is the
              thing that happened. Two bars side by side invite reading the
              gap as a quantity of its own. */}
          <Bar dataKey="Actual" fill={chart.categorical[0]} radius={[3, 3, 0, 0]}
               isAnimationActive={false} />
          <Line type="monotone" dataKey="Budget" stroke={chart.categorical[1]}
                strokeWidth={2} dot={{ r: 2 }} connectNulls={false}
                isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// A cost center's own year, drawn on expand. Deliberately no per-month colouring
// of the gap: at company scale a five-run month is a quarter over and the note
// explains it, but against one yard's base that same artefact reads as a
// blowout. The bar and the line are left to speak.
function CcMonths({ row, year, partialMonth }) {
  const rows = (row.months || []).map((m) => ({
    ...m,
    label:  MONTHS[m.month - 1],
    Budget: m.budget,
    Actual: m.actual,
  }));

  if (!rows.length) return <div style={s.ccEmpty}>No budget line and no overtime this year.</div>;

  return (
    <div style={s.ccPanel}>
      <MonthChart rows={rows} year={year} height={170} />
      <div style={s.ccNote}>
        {row.budget === null
          ? 'Not in the budget file, so there is no line to compare against — the bars are spending only.'
          : row.budgeted_months < 12
            ? `Budgeted for ${row.budgeted_months} of 12 months. The months with no budget line are left as gaps, not zeros.`
            : 'Budgeted for all twelve months.'}
        {partialMonth && ` ${MONTHS[partialMonth - 1]} is still filling and is held out of the figures in the row above.`}
      </div>
    </div>
  );
}

function Variance({ actual, budget, doubt = false }) {
  if (actual === null || budget === null || budget === undefined) {
    return <span style={s.faint}>—</span>;
  }
  const diff = actual - budget;
  if (Math.abs(diff) < 1) return <span style={s.faint}>on budget</span>;
  // Over budget is red without qualification: this panel is spend against a
  // commitment, and there is no reading in which spending more than the budget
  // is the better outcome.
  //
  // Under budget is NOT green without qualification. Past a certain distance an
  // underrun stops being performance and starts being a missing month or the
  // wrong ledger line, and colouring that green tells an executive they have
  // won something. `doubt` renders it plainly instead, with the note below
  // saying why.
  const over = diff > 0;
  const colour = over ? c.dangerText : (doubt ? c.textMuted : c.successText);
  return (
    <span style={{ ...s.variance, color: colour }}>
      {diff > 0 ? '▲' : '▼'} {fmtMoney(Math.abs(diff))}
      <span style={s.variancePct}>
        {budget ? ` (${((diff / budget) * 100).toFixed(1)}%)` : ''}
      </span>
    </span>
  );
}

function Tile({ label, value, sub, strong = false }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={strong ? s.tileValueStrong : s.tileValue}>{value}</div>
      {sub && <div style={s.tileSub}>{sub}</div>}
    </div>
  );
}

export default function BudgetVsActual({ year }) {
  const [openCc, setOpenCc] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-budget', year],
    queryFn:  () => api.get('/overtime/budget', { params: { year } }),
  });

  if (isLoading) return <div style={s.wrap}><LoadingSpinner /></div>;
  if (error)     return <div style={s.wrap}><div style={s.err}>{error.message}</div></div>;

  const budgetMeta = data?.budget || null;
  const months     = data?.months || [];

  // No budget loaded is a state, not an error, and it must not render as a
  // variance of minus everything. It also names its own fix.
  if (!budgetMeta) {
    return (
      <div style={s.wrap}>
        <div style={s.header}>
          <div style={s.title}>Overtime vs Budget</div>
          <div style={s.subtitle}>No budget has been loaded for {year}</div>
        </div>
        <div style={s.empty}>
          Load the year's budget under <strong>Administration → Overtime Budget</strong> and this
          panel fills in. Until then the board can say what was spent, but not whether that was
          the plan.
        </div>
      </div>
    );
  }

  const withActuals = months.filter((m) => m.actual !== null);
  const newest      = withActuals[withActuals.length - 1] || null;
  const runsSoFar   = newest ? newest.period_count : 0;
  const runsDue     = newest ? expectedRuns(newest.last_period_end, newest.month, year) : null;
  const newestPartial = runsDue !== null && runsSoFar < runsDue;

  // Year-to-date pairs the actuals with the budget for THE SAME months, never
  // with the whole year: a July board compared against an annual figure is
  // 58% under budget and always will be.
  //
  // And never with a month still filling up. A month enters this pair when its
  // pay runs are all in, not when its first one lands -- otherwise a month two
  // runs deep contributes a quarter of its spending against ALL of its budget,
  // and the variance reads as a saving that is really just a calendar. The
  // partial month is reported on its own line instead of being dropped, because
  // an executive who can see July on the chart will look for it here.
  const complete  = newestPartial ? withActuals.slice(0, -1) : withActuals;
  const actualYtd = complete.reduce((sum, m) => sum + m.actual, 0);
  const budgetYtd = complete.reduce((sum, m) => sum + (m.budget || 0), 0);

  const first = complete[0] || null;
  const last  = complete[complete.length - 1] || null;
  const span  = first && last
    ? (first.month === last.month ? MONTHS[first.month - 1] : `${MONTHS[first.month - 1]}–${MONTHS[last.month - 1]}`)
    : null;

  // Far enough under to be a question rather than an achievement. A board that
  // is genuinely spending half its overtime budget is not what this usually
  // means: it means payroll is missing for those months, or the file that was
  // loaded is a broader ledger line than overtime. Either way the honest thing
  // on screen is a doubt, not a green tick.
  const implausible = budgetYtd > 0 && actualYtd < budgetYtd * 0.5;

  const rows = months.map((m) => ({
    ...m,
    label:   MONTHS[m.month - 1],
    Budget:  m.budget,
    Actual:  m.actual,
  }));

  // Each cost center gets the SAME pair the tiles use -- complete months only --
  // rather than a part-year actual against a full-year budget, which was the old
  // column and could only ever say "comfortably under". A month counts when the
  // company's payroll for it is all in and this cost center has a budget line
  // for it; both sides then cover the identical months.
  //
  // Note which month is held out is decided once, company-wide. Doing it per
  // cost center would read "this yard had no overtime in August" as "August is
  // still filling here" and quietly drop real zero months out of the variance.
  const completeMonths = new Set(complete.map((m) => m.month));

  const ccRows = (data?.cost_centers || []).map((row) => {
    const paired = (row.months || []).filter((m) => completeMonths.has(m.month) && m.budget !== null);
    const ytdBudget = paired.reduce((sum, m) => sum + m.budget, 0);
    // A budgeted month with no overtime row is a true zero, not a gap: payroll
    // ran, this cost center simply did not spend. Gaps are excluded above by
    // requiring a budget line, so the `|| 0` here cannot swallow one.
    const ytdActual = paired.reduce((sum, m) => sum + (m.actual || 0), 0);
    const unbudgeted = (row.months || [])
      .filter((m) => completeMonths.has(m.month) && m.budget === null && m.actual !== null);
    const unbudgetedSpend = unbudgeted.reduce((sum, m) => sum + m.actual, 0);
    // What the cost center spent across the complete months whatever its budget
    // says. Shown when there is no pairing at all, so that a cost center missing
    // from the budget file still reports its spending rather than an em dash.
    const completeActual = ytdActual + unbudgetedSpend;
    return {
      ...row,
      pairedMonths: paired.length,
      ytdBudget,
      ytdActual,
      unbudgetedSpend,
      completeActual,
      // The same doubt the header applies, asked per row. A missing payroll run
      // or a wrong ledger line is likelier true of one yard than of the company,
      // so this is the level it should actually be caught at.
      // A budgeted cost center with no overtime row ANYWHERE this year is not
      // the same fact as one that spent nothing in these months, and the route
      // already tells them apart: `actual` is null for the first and a number
      // for the second. Drawing the first as 100% under budget would put the
      // largest "saving" on the board next to the yards that actually earned
      // theirs -- and it is far likelier that payroll for it has not landed, or
      // that it maps to a different code in the overtime file.
      noneRecorded: row.actual === null,
      doubt:    paired.length > 0 && ytdBudget > 0 && ytdActual < ytdBudget * 0.5,
      variance: paired.length > 0 && row.actual !== null ? ytdActual - ytdBudget : null,
    };
  }).sort((a, b) => {
    // Sorted on a real variance, this is the ranked who-is-over list. Rows with
    // nothing to compare cannot be ranked against it and go below, by spend.
    if ((a.variance === null) !== (b.variance === null)) return a.variance === null ? 1 : -1;
    if (a.variance === null) return (a.noneRecorded === b.noneRecorded) ? 0 : (a.noneRecorded ? 1 : -1);
    if (a.variance !== null) return b.variance - a.variance;
    return b.completeActual - a.completeActual;
  });

  const noBudgetCount     = ccRows.filter((row) => row.budget === null && row.actual !== null).length;
  const noneRecordedCount = ccRows.filter((row) => row.noneRecorded && row.budget !== null).length;
  const overCount     = ccRows.filter((row) => row.variance !== null && row.variance > 0).length;

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.title}>Overtime vs Budget — {year}</div>
        <div style={s.subtitle}>
          Every cost center you can see, whole — this panel does not follow the lens or the
          labour-category filter, because the budget has no such split.
        </div>
      </div>

      <div style={s.tiles}>
        <Tile label={span ? `Actual, ${span}` : 'Actual'}
              value={complete.length ? fmtMoney(actualYtd) : <span style={s.faint}>—</span>} strong
              sub={complete.length
                ? `${complete.length} complete month${complete.length === 1 ? '' : 's'}`
                : 'no complete month yet'} />
        <Tile label={span ? `Budget, ${span}` : 'Budget'}
              value={complete.length ? fmtMoney(budgetYtd) : <span style={s.faint}>—</span>} />
        <Tile label="Variance"
              value={complete.length
                ? <Variance actual={actualYtd} budget={budgetYtd} doubt={implausible} />
                : <span style={s.faint}>—</span>}
              sub={newestPartial
                ? `${MONTHS[newest.month - 1]} is still filling — ${runsSoFar} of ${runsDue} runs, held out`
                : 'complete months only'} />
        <Tile label="Full-year budget" value={fmtMoney(budgetMeta.total_amount)}
              sub={budgetMeta.file_name} />
      </div>

      {/* The month the chart shows and the tiles deliberately exclude. */}
      {newestPartial && (
        <div style={s.partialRow}>
          {MONTHS[newest.month - 1]} so far: {fmtMoney(newest.actual)} against a full-month budget of{' '}
          {newest.budget === null ? '—' : fmtMoney(newest.budget)} — {runsSoFar} of {runsDue} pay runs in,
          so the two are not yet comparable.
        </div>
      )}

      {implausible && complete.length > 0 && (
        <div style={s.doubt}>
          <AlertTriangle size={14} />
          <span>
            Overtime is running at {((actualYtd / budgetYtd) * 100).toFixed(0)}% of budget across{' '}
            {complete.length} complete month{complete.length === 1 ? '' : 's'}. A gap that size is more
            often missing payroll or a budget line broader than overtime than it is a genuine saving —
            worth confirming before it is reported as one.
          </span>
        </div>
      )}

      <MonthChart rows={rows} year={year} height={220} />

      <div style={s.runNote}>
        A month is the pay runs that ended in it, so a five-run month carries about a quarter
        more payroll than a four-run one against the same calendar budget. The run count is in
        each month's tooltip; the year-to-date figures above are unaffected by it.
      </div>

      <DataDisclosure label="cost center" count={ccRows.length}>
        <div style={s.tableNote}>
          Each cost center against {span ? `its own ${span} budget` : 'its own budget'}, complete
          months only — the same pair as the tiles above, so the rows and the header agree. Sorted
          by how far over.{overCount > 0 && ` ${overCount} of ${ccRows.length} are over.`}
        </div>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Cost Center</th>
                <th style={s.thR}>{span ? `Budget, ${span}` : 'Budget'}</th>
                <th style={s.thR}>{span ? `Actual, ${span}` : 'Actual'}</th>
                <th style={s.thR}>Variance</th>
                <th style={s.thR}>Full-year budget</th>
              </tr>
            </thead>
            <tbody>
              {ccRows.map((row) => {
                const open = openCc === row.cc_id;
                return (
                  <Fragment key={row.cc_id}>
                    <tr style={open ? { ...s.tr, ...s.trOpen } : s.tr}>
                      <td style={s.td}>
                        <button type="button" style={s.expand}
                                aria-expanded={open}
                                onClick={() => setOpenCc(open ? null : row.cc_id)}>
                          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          <span>{row.code} · {row.name}</span>
                        </button>
                      </td>
                      <td style={s.tdR}>
                        {row.pairedMonths === 0
                          ? <span style={s.faint}>—</span>
                          : fmtMoney(row.ytdBudget)}
                        {row.pairedMonths > 0 && row.pairedMonths < complete.length && (
                          <div style={s.cellSub}>{row.pairedMonths} of {complete.length} months budgeted</div>
                        )}
                      </td>
                      <td style={s.tdR}>
                        {row.pairedMonths > 0
                          ? fmtMoney(row.ytdActual)
                          : row.completeActual > 0
                            ? <span style={s.faint} title="Spent in months this cost center has no budget line for">
                                {fmtMoney(row.completeActual)}
                              </span>
                            : <span style={s.faint}>—</span>}
                        {row.pairedMonths > 0 && row.unbudgetedSpend > 0 && (
                          <div style={s.cellSub}>+{fmtMoney(row.unbudgetedSpend)} in unbudgeted months</div>
                        )}
                      </td>
                      <td style={s.tdR}>
                        {row.noneRecorded
                          ? <span style={s.faint} title="Budgeted, but this cost center has no overtime record at all this year">
                              none recorded
                            </span>
                          : row.pairedMonths === 0
                            ? <span style={s.faint} title="No month where this cost center has both a budget line and completed payroll">
                                nothing to compare
                              </span>
                            : <Variance actual={row.ytdActual} budget={row.ytdBudget} doubt={row.doubt} />}
                      </td>
                      <td style={s.tdR}>
                        {row.budget === null
                          ? <span style={s.faint} title="This cost center is not in the budget file">not budgeted</span>
                          : fmtMoney(row.budget)}
                        {row.budget !== null && row.budgeted_months < 12 && (
                          <div style={s.cellSub}>{row.budgeted_months} of 12 months</div>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td style={s.ccCell} colSpan={5}>
                          <CcMonths row={row} year={year}
                                    partialMonth={newestPartial ? newest.month : null} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {noneRecordedCount > 0 && (
          <div style={s.footnote}>
            {noneRecordedCount} budgeted cost center{noneRecordedCount === 1 ? ' has' : 's have'} no
            overtime record at all this year. Shown as unrecorded rather than as a saving of their
            whole budget — a cost center that maps to a different code in the overtime file looks
            exactly like this.
          </div>
        )}
        {noBudgetCount > 0 && (
          <div style={s.footnote}>
            {noBudgetCount} cost center{noBudgetCount === 1 ? '' : 's'} spent overtime with no line in
            the budget file. That is not an overrun — it is a gap between the two lists, and worth
            asking finance about.
          </div>
        )}
      </DataDisclosure>
    </div>
  );
}

const s = {
  wrap:     { ...card.base, overflow: 'hidden', marginBottom: sp[5] },
  header:   { padding: `${sp[4]} ${sp[5]} ${sp[2]}` },
  title:    { ...t.h3, color: c.navy },
  subtitle: { ...t.micro, color: c.textFaint, marginTop: '2px' },
  err:      { ...t.caption, color: c.dangerText, padding: sp[5] },
  empty:    { ...t.caption, color: c.textMuted, padding: `0 ${sp[5]} ${sp[5]}`, lineHeight: 1.6 },
  tiles:    {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: sp[3], padding: `0 ${sp[5]} ${sp[3]}`,
  },
  tile:       { background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: r.md, padding: sp[3] },
  tileLabel:  { ...t.micro, color: c.textFaint },
  tileValue:  { ...t.h3, color: c.text, marginTop: '2px' },
  tileValueStrong: { ...t.h2, color: c.navy, marginTop: '2px' },
  tileSub:    { ...t.micro, color: c.textFaint, marginTop: '2px' },
  chartWrap:  { padding: `${sp[2]} ${sp[3]} 0` },
  partialRow: {
    ...t.micro, color: c.textMuted, padding: `0 ${sp[5]} ${sp[3]}`, lineHeight: 1.6,
  },
  doubt: {
    display: 'flex', gap: sp[2], alignItems: 'flex-start', ...t.caption, color: c.warningText,
    background: c.warningTint, border: `1px solid ${c.warning}`, borderRadius: r.md,
    padding: sp[3], margin: `0 ${sp[5]} ${sp[4]}`, lineHeight: 1.6,
  },
  runNote:    { ...t.micro, color: c.textMuted, padding: `${sp[2]} ${sp[5]} ${sp[3]}`, lineHeight: 1.6 },
  tableNote:  { ...t.micro, color: c.textMuted, padding: `0 ${sp[5]} ${sp[3]}`, lineHeight: 1.6 },
  scroll:     { overflowX: 'auto' },
  expand:     {
    display: 'inline-flex', alignItems: 'center', gap: sp[2], background: 'none',
    border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit',
    textAlign: 'left',
  },
  trOpen:     { background: c.surfaceSunk },
  ccCell:     { padding: 0, background: c.surfaceSunk, borderBottom: `1px solid ${c.border}` },
  ccPanel:    { padding: `${sp[3]} 0 ${sp[2]}` },
  ccNote:     { ...t.micro, color: c.textMuted, padding: `0 ${sp[5]} ${sp[3]}`, lineHeight: 1.6 },
  ccEmpty:    { ...t.micro, color: c.textFaint, padding: sp[4] },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { ...tbl.th, whiteSpace: 'nowrap' },
  thR:        { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:         { ...tbl.tr },
  td:         { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:        { ...tbl.tdR },
  cellSub:    { ...t.micro, color: c.textFaint, fontWeight: 400 },
  faint:      { color: c.textFaint },
  variance:   { ...t.tabular, fontWeight: 600 },
  variancePct:{ ...t.micro, fontWeight: 400 },
  footnote:   { ...t.micro, color: c.textMuted, padding: `${sp[2]} ${sp[5]} ${sp[4]}`, lineHeight: 1.6 },
};
